import React from "react";

/**
 * Root-level error boundary. Prevents a single component crash from blanking
 * the entire app. Renders a polite recovery screen with a "Reload" button.
 *
 * Bonus: detects chunk-load errors (the classic symptom of a stale Service
 * Worker after a deploy with new bundle hashes) and auto-recovers ONCE by
 * unregistering all SWs + nuking the Cache API + hard-reloading the page.
 * The sessionStorage guard prevents an infinite reload loop if the error
 * actually IS a code bug, not a cache mismatch.
 */
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk/i,
  /ChunkLoadError/i,
  /Cannot find module/i,                // webpack lazy-chunk failed to resolve
  /Failed to fetch dynamically imported module/i,  // vite-style code-split
  /error loading dynamically imported module/i,
  /\.chunk\.js/i,
];

const RECOVERY_FLAG = "__xyz_chunk_recovered__";

function isChunkError(err) {
  if (!err) return false;
  const msg = (err.message || err.toString() || "").trim();
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
}

async function nukeClientCachesAndReload() {
  try {
    sessionStorage.setItem(RECOVERY_FLAG, String(Date.now()));
    // 1. Unregister every service worker so the stale chunk index goes away.
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    // 2. Wipe the Cache API so old hashed chunks don't get re-served.
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => {})));
    }
  } catch { /* best-effort — proceed to reload regardless */ }
  // 3. Hard reload (bypasses HTTP cache where supported).
  window.location.reload();
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Chunk error auto-recovery — only ONCE per session to avoid loops.
    if (isChunkError(error)) {
      const already = (() => { try { return sessionStorage.getItem(RECOVERY_FLAG); } catch { return null; } })();
      if (!already) {
        nukeClientCachesAndReload();
      }
    }
  }

  handleReload = () => {
    // Force-flush caches on manual reload too — if the user is seeing this
    // screen at all, odds are something is stale.
    nukeClientCachesAndReload();
  };

  handleBack = () => {
    this.setState({ hasError: false, error: null });
    window.history.back();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const chunky = isChunkError(this.state.error);
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="max-w-md w-full text-center">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">
            XYZPLUS · Something went wrong
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
            {chunky ? "Refreshing the app…" : "We hit a snag."}
          </h1>
          <p className="text-sm text-zinc-500 mb-6">
            {chunky
              ? "A new version of the site is available. Clearing your cache and reloading automatically — give it a couple of seconds."
              : "The last action could not be completed. Your data is safe — please reload the page or go back and try again."}
          </p>
          {!chunky && this.state.error?.message && (
            <pre className="text-[11px] text-left bg-zinc-50 border border-zinc-200 p-3 mb-6 rounded overflow-auto max-h-32">
              {String(this.state.error.message).slice(0, 400)}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleBack}
              className="h-11 px-5 border border-zinc-300 text-sm font-bold uppercase tracking-wider hover:border-[#111111]"
              data-testid="error-boundary-back"
            >
              Go back
            </button>
            <button
              onClick={this.handleReload}
              className="h-11 px-5 bg-[#FF5722] text-white text-sm font-bold uppercase tracking-wider hover:bg-orange-600"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
